import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { PageHero } from "@/components/Section";
import { SEO } from "@/components/SEO";
import api, { formatApiError } from "@/lib/api";
import { REGIONS, LOGO_URL, SITE_URL } from "@/lib/constants";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Heart, Users, HandHeart, Info } from "lucide-react";

const empty = { full_name: "", email: "", phone: "", region: "", city: "", membership_type: "adherent", company: "" };

const TYPES = [
  { key: "adherent", Icon: Users, label: "Adhérent", desc: "Devenez membre à part entière du mouvement." },
  { key: "sympathisant", Icon: Heart, label: "Sympathisant", desc: "Soutenez le mouvement et restez informé." },
  { key: "benevole", Icon: HandHeart, label: "Bénévole", desc: "Donnez de votre temps sur le terrain." },
];

export default function Adhesion() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...empty, membership_type: params.get("type") === "soutien" ? "sympathisant" : "adherent" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/membership", form);
      setResult(data);
      toast.success("Adhésion enregistrée !");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div data-testid="adhesion-success" className="bg-brand-surface min-h-[70vh]">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center">
            <CheckCircle2 size={56} className="mx-auto text-brand-green" />
            <h1 className="mt-5 text-3xl font-bold text-slate-900">Bienvenue chez DEFAR !</h1>
            <p className="mt-3 text-slate-600">Votre adhésion a bien été enregistrée. Voici votre carte de membre digitale.</p>
          </div>

          {/* Digital member card */}
          <div className="mt-10 max-w-md mx-auto bg-gradient-to-br from-brand-blue to-brand-green text-white rounded-lg overflow-hidden shadow-xl" data-testid="member-card">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <img src={LOGO_URL} alt="DEFAR" className="h-10 bg-white/90 p-1 rounded" />
                <span className="text-xs uppercase tracking-widest text-emerald-100">Carte de membre</span>
              </div>
              <div className="mt-8 flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs text-emerald-100 uppercase tracking-wide">Membre</p>
                  <p className="text-xl font-bold">{result.full_name}</p>
                  <p className="mt-3 text-xs text-emerald-100 uppercase tracking-wide">N° d'adhérent</p>
                  <p className="font-mono text-lg font-semibold" data-testid="member-number">{result.member_number}</p>
                  <p className="mt-2 text-xs text-emerald-100 capitalize">{result.membership_type}</p>
                </div>
                <div className="bg-white p-2 rounded">
                  <QRCodeCanvas value={`${SITE_URL}/verifier/${result.member_number}`} size={88} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white border border-border rounded-sm p-5 flex items-start gap-3 max-w-md mx-auto">
            <Info size={20} className="text-brand-blue shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600">
              Le paiement de la cotisation (Stripe, Wave, Orange Money) sera bientôt disponible.
              Un reçu vous sera envoyé par email dès l'activation de ce service.
            </p>
          </div>

          <div className="mt-6 text-center">
            <button onClick={() => { setResult(null); setForm(empty); }} className="text-brand-blue font-semibold text-sm">
              Enregistrer une nouvelle adhésion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="adhesion-page">
      <PageHero overline="Adhésion" title="Rejoignez DEFAR" subtitle="Devenez acteur du changement. Remplissez le formulaire ci-dessous pour rejoindre le mouvement et recevoir votre carte de membre digitale." />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {TYPES.map(({ key, Icon, label, desc }) => (
            <button
              key={key}
              type="button"
              data-testid={`type-${key}`}
              onClick={() => setForm({ ...form, membership_type: key })}
              className={`text-left p-5 border-2 rounded-sm transition-colors ${
                form.membership_type === key ? "border-brand-green bg-brand-green/5" : "border-border hover:border-slate-300"
              }`}
            >
              <Icon size={22} className={form.membership_type === key ? "text-brand-green" : "text-slate-400"} />
              <p className="mt-3 font-bold text-slate-900">{label}</p>
              <p className="mt-1 text-xs text-slate-500 leading-relaxed">{desc}</p>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white border border-border rounded-sm p-6 sm:p-8 space-y-5">
          <input type="text" value={form.company} onChange={set("company")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Nom complet" value={form.full_name} onChange={set("full_name")} required testid="adh-name" />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="adh-email" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Téléphone" value={form.phone} onChange={set("phone")} testid="adh-phone" />
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Région</label>
              <select value={form.region} onChange={set("region")} data-testid="adh-region" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue">
                <option value="">Choisir une région</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <Field label="Ville" value={form.city} onChange={set("city")} testid="adh-city" />

          <button type="submit" disabled={loading} data-testid="adh-submit" className="inline-flex items-center gap-2 w-full justify-center bg-brand-green text-white hover:bg-brand-greenhover transition-colors py-3.5 font-semibold disabled:opacity-60">
            <CreditCard size={18} /> {loading ? "Traitement..." : "Valider mon adhésion"}
          </button>
          <p className="text-xs text-slate-400 text-center">Vos données sont traitées de manière confidentielle conformément à notre politique de protection des données.</p>
        </form>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && " *"}</label>
    <input type={type} required={required} value={value} onChange={onChange} data-testid={testid} className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
  </div>
);
